import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router';
import { ShieldCheckIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { API_BASE_URL } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { CfAccessPrompt } from './CfAccessPrompt';
export function AccessHandshake() {
    const accessToken = useAuthStore((s) => s.accessToken);
    const setAuth = useAuthStore((s) => s.setAuth);
    const [phase, setPhase] = useState('exchanging');
    const [errorDetail, setErrorDetail] = useState(null);
    const ran = useRef(false);
    useEffect(() => {
        if (accessToken)
            return;
        if (ran.current)
            return;
        ran.current = true;
        (async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/admin/session/exchange`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { Accept: 'application/json' },
                });
                if (res.ok) {
                    const data = (await res.json());
                    if (!data?.accessToken || !data.user) {
                        setPhase('failed');
                        setErrorDetail('Exchange succeeded but returned an unexpected payload.');
                        return;
                    }
                    setAuth({
                        accessToken: data.accessToken,
                        user: {
                            id: data.user.id,
                            email: data.user.email,
                            firstName: data.user.firstName,
                            lastName: data.user.lastName,
                            roles: data.user.roles,
                        },
                    });
                    // Component re-renders with accessToken set; <Outlet /> renders the app.
                    return;
                }
                if (res.status === 401 || res.status === 503) {
                    // 401 = CF Access JWT missing/invalid. Most likely the user reached
                    // this page without CF Access in the path (local dev, or a config
                    // gap). Fall back to legacy /login.
                    // 503 = backend has no CF Access config yet. Same fallback.
                    setPhase('fallback');
                    return;
                }
                if (res.status === 403) {
                    // CF Access verified but the email isn't a Wide Watch staff member.
                    let body = null;
                    try {
                        body = await res.json();
                    }
                    catch {
                        /* ignore */
                    }
                    setErrorDetail(body?.error ?? 'You are signed in to Cloudflare Access but are not authorized as Wide Watch staff.');
                    setPhase('forbidden');
                    return;
                }
                setErrorDetail(`Exchange returned HTTP ${res.status}.`);
                setPhase('failed');
            }
            catch (err) {
                setErrorDetail(err instanceof Error ? err.message : 'Network error during session exchange.');
                setPhase('failed');
            }
        })();
    }, [accessToken, setAuth]);
    if (accessToken) {
        return _jsx(Outlet, {});
    }
    if (phase === 'fallback') {
        // No CF Access JWT in the path (expired cookie / opened off-hostname) or the
        // backend isn't configured yet. Arthur has no in-app login — prompt to
        // re-run Cloudflare Access SSO (Duo), matching the other Arthur consoles.
        return _jsx(CfAccessPrompt, {});
    }
    if (phase === 'forbidden') {
        return _jsx(ErrorCard, { variant: "forbidden", detail: errorDetail });
    }
    if (phase === 'failed') {
        return _jsx(ErrorCard, { variant: "failed", detail: errorDetail });
    }
    return _jsx(LoadingCard, {});
}
function LoadingCard() {
    return (_jsx("div", { className: "min-h-screen grid place-items-center p-6", children: _jsxs("div", { className: "surface-card w-full max-w-md p-8 space-y-4 text-center", children: [_jsx("div", { className: "mx-auto grid h-12 w-12 place-items-center rounded-[var(--r-md)] border border-[var(--lav-200)] text-[var(--royal)]", style: {
                        background: 'linear-gradient(135deg, var(--lav-100), var(--lav-50))',
                    }, children: _jsx(ShieldCheckIcon, { className: "h-5 w-5" }) }), _jsx("h1", { className: "m-0 font-normal tracking-[-0.02em] text-[var(--ink-900)] [font-size:clamp(22px,2.6vw,28px)] leading-tight", style: { fontFamily: 'var(--f-display)' }, children: "Verifying with Cloudflare Access\u2026" }), _jsx("p", { className: "text-[14px] text-[var(--ink-500)]", children: "Checking your single sign-on session." })] }) }));
}
function ErrorCard({ variant, detail }) {
    return (_jsx("div", { className: "min-h-screen grid place-items-center p-6", children: _jsxs("div", { className: "surface-card w-full max-w-md p-8 space-y-4 text-center", children: [_jsx("div", { className: "mx-auto grid h-12 w-12 place-items-center rounded-[var(--r-md)] border border-[var(--rose-200)] text-[var(--crimson)] [background:linear-gradient(135deg,var(--rose-100),var(--rose-50))]", children: _jsx(ShieldExclamationIcon, { className: "h-5 w-5" }) }), _jsx("h1", { className: "m-0 font-normal tracking-[-0.02em] text-[var(--ink-900)] [font-size:clamp(22px,2.6vw,28px)] leading-tight", style: { fontFamily: 'var(--f-display)' }, children: variant === 'forbidden' ? 'Not Wide Watch staff' : 'Session exchange failed' }), _jsx("p", { className: "text-[14px] text-[var(--ink-500)]", children: detail
                        ?? (variant === 'forbidden'
                            ? 'You signed in to Cloudflare Access but your email is not on the Wide Watch staff allowlist.'
                            : 'Something went wrong checking your session. Try reloading.') }), variant === 'failed' && (_jsx("button", { type: "button", onClick: () => window.location.reload(), className: "text-[13px] font-medium text-[var(--royal-text)] underline", children: "Reload" }))] }) }));
}
